import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, EnvelopeSimple, LockKey, UserPlus, UsersThree, CheckCircle, ShieldCheck, Star, Phone, Clipboard, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Logo, NEUTRAL_NAME, DEFAULT_CLAIM } from "../components/Logo";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../components/ui/tooltip";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function InfoHint({ text }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { if (!open) return; const t = setTimeout(() => setOpen(false), 6000); return () => clearTimeout(t); }, [open]);
  return (
    <TooltipProvider delayDuration={80}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <button type="button" data-testid="empfehlen-dsgvo-hint" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }} className="ml-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full align-middle text-[#A0AEC0] hover:text-[#C5A059]"><Info size={13} weight="bold" /></button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[240px] text-left leading-snug">{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function Empfehlen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const modus = params.get("modus");
  const pmode = params.get("pmode");
  const { user, login } = useAuth();

  // flow: kunde | karriere_interesse | karriere_empfehlung
  const [flow, setFlow] = useState(modus === "karriere" ? null : "kunde");
  // step: karriere_choose | form | karriere_form | email | code | done
  const [step, setStep] = useState(modus === "karriere" ? "karriere_choose" : "form");

  const [form, setForm] = useState({ kontakt_name: "", kontakt_telefon: "", kontakt_email: "", anliegen: "" });
  const [karriereForm, setKarriereForm] = useState({ name: "", kontakt: "", notiz: "" });
  const [zustimmung, setZustimmung] = useState(false);
  const [email, setEmail] = useState(user?.email || "");
  const [werberName, setWerberName] = useState(user?.name || "");
  const [werberTel, setWerberTel] = useState(user?.telefon || "");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const isLoggedKunde = user && user.rolle === "kunde";

  const [advisorName, setAdvisorName] = useState(null);
  const [advisorPraemieAktiv, setAdvisorPraemieAktiv] = useState(true);
  const [advisorClaim, setAdvisorClaim] = useState("");
  const [advisorThemen, setAdvisorThemen] = useState([]);
  const [advisorPraemieTyp, setAdvisorPraemieTyp] = useState("geld");
  const [advisorSachOptionen, setAdvisorSachOptionen] = useState([]);
  const [wunschSache, setWunschSache] = useState("");
  useEffect(() => {
    if (ref) {
      api.get(`/berater/public/${ref}`).then(({ data }) => { if (data.name) setAdvisorName(data.name); setAdvisorPraemieAktiv(pmode === "aus" ? false : pmode === "an" ? true : (data.praemie_aktiv !== false)); setAdvisorClaim(data.claim || ""); setAdvisorThemen(data.themen || []); setAdvisorPraemieTyp(data.praemie_typ || "geld"); setAdvisorSachOptionen(data.sach_optionen || []); setWunschSache(data.praemie_sache || ""); }).catch(() => {});
    }
  }, [ref]);

  const runAction = async () => {
    if (flow === "kunde") await api.post("/referrals", { ...form, zustimmung: true, werber_name: werberName, werber_telefon: werberTel, praemie_modus: pmode, praemie_sache_wunsch: wunschSache });
    else if (flow === "karriere_interesse") await api.post("/karriere-interesse");
    else if (flow === "karriere_empfehlung") await api.post("/karriere-empfehlung", karriereForm);
  };

  // --- flow starters ---
  const chooseInteresse = async () => {
    setFlow("karriere_interesse");
    if (isLoggedKunde) return submitDirect("karriere_interesse");
    setStep("email");
  };
  const chooseEmpfehlung = () => { setFlow("karriere_empfehlung"); setStep("karriere_form"); };

  const submitDirect = async (f) => {
    setLoading(true);
    try {
      if (f === "karriere_interesse") await api.post("/karriere-interesse");
      else if (f === "kunde") await api.post("/referrals", { ...form, zustimmung: true, werber_name: werberName, werber_telefon: werberTel, praemie_modus: pmode, praemie_sache_wunsch: wunschSache });
      else if (f === "karriere_empfehlung") await api.post("/karriere-empfehlung", karriereForm);
      setStep("done");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitKundeForm = async (e) => {
    e.preventDefault();
    if (!zustimmung) return toast.error("Bitte bestätige die Zustimmung der empfohlenen Person.");
    if (isLoggedKunde) return submitDirect("kunde");
    setStep("email");
  };

  const submitKarriereForm = (e) => {
    e.preventDefault();
    if (!karriereForm.name.trim() || !karriereForm.kontakt.trim()) return toast.error("Bitte Name und Kontakt angeben.");
    if (!zustimmung) return toast.error("Bitte bestätige, dass du die Person informiert hast.");
    if (isLoggedKunde) return submitDirect("karriere_empfehlung");
    setStep("email");
  };

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/request-code", { email, ref_code: ref });
      setStep("code");
      setCooldown(30);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    if (cooldown > 0 || loading) return;
    setLoading(true);
    try {
      await api.post("/auth/request-code", { email, ref_code: ref });
      setCooldown(30);
      toast.success("Neuer Code wurde gesendet.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyAndSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-code", { email, code });
      login(data.token, data.user);
      await runAction();
      setStep("done");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const pasteCode = async () => {
    try {
      const t = await navigator.clipboard.readText();
      const digits = (t.match(/\d/g) || []).join("").slice(0, 6);
      if (!digits) return toast.error("Kein Code in der Zwischenablage gefunden.");
      setCode(digits);
      toast.success("Code eingefügt.");
    } catch {
      toast.error("Einfügen nicht möglich. Bitte Code manuell eintippen.");
    }
  };

  const back = () => {
    if (step === "karriere_choose") return navigate(ref ? `/start/${ref}` : "/");
    if (step === "form") return navigate(ref ? `/start/${ref}` : "/");
    if (step === "karriere_form") return setStep("karriere_choose");
    if (step === "email") {
      if (flow === "kunde") return setStep("form");
      if (flow === "karriere_empfehlung") return setStep("karriere_form");
      return setStep("karriere_choose");
    }
    if (step === "code") return setStep("email");
  };

  const showStepper = ["form", "karriere_form", "email", "code"].includes(step);
  const stepperKeys = flow === "kunde" ? ["form", "email", "code"] : flow === "karriere_empfehlung" ? ["karriere_form", "email", "code"] : ["email", "code"];

  return (
    <div className="mx-auto min-h-screen max-w-md bg-[#F8F9FA]">
      <div className="flex items-center justify-between px-6 pt-[calc(env(safe-area-inset-top)+1.25rem)]">
        {step !== "done" ? (
          <button data-testid="back-btn" onClick={back} className="flex h-10 items-center gap-1.5 rounded-full border border-[#0A1128]/15 bg-white pl-2 pr-3.5 text-sm font-semibold text-[#0A1128] shadow-sm transition-colors hover:bg-black/5">
            <ArrowLeft size={20} /> Zurück
          </button>
        ) : <div className="w-10" />}
        <Logo {...(advisorPraemieAktiv === false ? { name: NEUTRAL_NAME, claim: advisorClaim || DEFAULT_CLAIM } : { claim: advisorClaim || "" })} />
        <div className="w-10" />
      </div>

      <div className="px-7 pt-8">
        {advisorName && step !== "done" && (
          <div className="mb-6 flex items-center gap-2.5 rounded-full border border-[#C5A059]/30 bg-[#FBF8F1] px-4 py-2" data-testid="empfehlen-advisor">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#C5A059] text-[10px] font-extrabold text-[#0A1128]">
              {advisorName.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("")}
            </div>
            <span className="truncate text-xs font-semibold text-[#8a6d2f]">Empfehlung für {advisorName.replace(/\n/g, " ")}</span>
          </div>
        )}
        {showStepper && (
          <div className="mb-8 flex items-center gap-2">
            {stepperKeys.map((s, i) => {
              const active = i <= stepperKeys.indexOf(step);
              return <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${active ? "bg-[#C5A059]" : "bg-[#E2E8F0]"}`} />;
            })}
          </div>
        )}

        {step === "karriere_choose" && (
          <motion.div key="karriere_choose" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="karriere-choose-step">
            <div className="mb-1 h-px w-12 bg-[#C5A059]" />
            <h1 className="font-display text-4xl font-light tracking-tight text-[#0A1128]">Job &amp; Karriere</h1>
            <p className="text-base text-[#718096]">Der Beraterberuf: flexibel, selbstbestimmt und mit echtem Einkommenspotenzial – bei uns möglich in Voll- oder Teilzeit, haupt- oder nebenberuflich als zweites Standbein, mit schrittweiser Einarbeitung an deiner Seite.</p>
            <div className="space-y-3 pt-2">
              <ChoiceTile testid="choice-interesse" icon={<Star size={26} weight="duotone" color="#C5A059" />} title="Ich interessiere mich" text="Unverbindlich mehr über den Beraterberuf erfahren." onClick={chooseInteresse} />
              <ChoiceTile testid="choice-empfehlung" icon={<UsersThree size={26} weight="duotone" color="#C5A059" />} title="Ich kenne jemanden" text="Eine Person aus deinem Umfeld für den Job empfehlen." onClick={chooseEmpfehlung} />
            </div>
          </motion.div>
        )}

        {step === "form" && (
          <motion.form key="form" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} onSubmit={submitKundeForm} className="space-y-4" data-testid="referral-form">
            <div className="mb-1 h-px w-12 bg-[#C5A059]" />
            <h1 className="font-display text-4xl font-light tracking-tight text-[#0A1128]">Kontakt empfehlen</h1>
            <p className="text-base text-[#718096]">Wen dürfen wir beraten? Wir melden uns diskret.</p>
            <div className="space-y-4 pt-2">
              <Field label="Name des Kontakts" testid="input-kontakt_name" value={form.kontakt_name} onChange={(v) => setForm({ ...form, kontakt_name: v })} placeholder="Max Mustermann" />
              <Field label="Telefon" testid="input-kontakt_telefon" type="tel" value={form.kontakt_telefon} onChange={(v) => setForm({ ...form, kontakt_telefon: v })} placeholder="+49 …" />
              <Field label="E-Mail" testid="input-kontakt_email" type="email" value={form.kontakt_email} onChange={(v) => setForm({ ...form, kontakt_email: v })} placeholder="kontakt@beispiel.de" />
              {advisorThemen.length > 0 && (
                <div data-testid="anliegen-block">
                  <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.1em] text-[#C5A059]">Worum geht es? (optional)</label>
                  <div className="flex flex-wrap gap-2">
                    {advisorThemen.map((t, i) => (
                      <button type="button" key={i} data-testid={`anliegen-chip-${i}`} onClick={() => setForm({ ...form, anliegen: form.anliegen === t ? "" : t })} className={`rounded-full border px-3 py-1.5 text-left text-xs font-medium transition-colors ${form.anliegen === t ? "border-[#C5A059] bg-[#FBF8F1] text-[#8a6d2f]" : "border-[#E2E8F0] bg-white text-[#718096]"}`}>{t}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {advisorPraemieAktiv !== false && advisorPraemieTyp === "sache" && advisorSachOptionen.length > 0 && (
              <div data-testid="wunsch-block">
                <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.1em] text-[#C5A059]">Deine Wunsch-Prämie 🎁</label>
                <select data-testid="wunsch-sache-select" value={wunschSache} onChange={(e) => setWunschSache(e.target.value)} className="h-11 w-full rounded-lg border border-[#E2E8F0] bg-white px-3 text-sm text-[#0A1128] focus:border-[#C5A059] focus:outline-none">
                  {advisorSachOptionen.map((s) => (<option key={s} value={s}>{s}</option>))}
                </select>
                <p className="mt-1 text-[11px] leading-snug text-[#A0AEC0]">Als Dankeschön ist eine Sachprämie vorgesehen – wähle, worüber du dich am meisten freust.</p>
              </div>
            )}
            <label className="flex cursor-pointer items-start gap-3 rounded-xl bg-white p-4 ring-1 ring-[#E2E8F0]">
              <Checkbox data-testid="zustimmung-checkbox" checked={zustimmung} onCheckedChange={setZustimmung} className="mt-0.5 h-5 w-5 border-[#C5A059] data-[state=checked]:border-[#C5A059] data-[state=checked]:bg-[#C5A059]" />
              <span className="text-sm leading-relaxed text-[#4A5568]">Die empfohlene Person erhält eine kurze E-Mail und muss darin die Weitergabe ihrer Kontaktdaten bestätigen (DSGVO-konform). Ihr Name und ihre Kontaktdaten werden erst nach ihrer eigenen Freigabe angezeigt.<InfoHint text="DSGVO: Der Name und die Kontaktdaten der empfohlenen Person werden erst sichtbar, nachdem sie selbst per E-Mail bestätigt (freigegeben) hat. Ohne Freigabe erfolgt keine Anzeige und keine Kontaktaufnahme." /></span>
            </label>
            <button data-testid="submit-referral-btn" type="submit" disabled={loading} className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[#0A1128] text-base font-semibold text-white transition-all duration-200 active:scale-[0.98] disabled:opacity-60">
              <UserPlus size={20} /> {loading ? "…" : isLoggedKunde ? "Empfehlung senden" : "Weiter"}
            </button>
            <p className="text-center text-[11px] leading-relaxed text-[#A0AEC0]" data-testid="datenschutz-hinweis-form">Wir verarbeiten die Daten DSGVO-konform. Die empfohlene Person bestätigt die Weitergabe selbst per E-Mail; ohne Bestätigung erfolgt keine Kontaktaufnahme.</p>
          </motion.form>
        )}

        {step === "karriere_form" && (
          <motion.form key="karriere_form" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} onSubmit={submitKarriereForm} className="space-y-4" data-testid="karriere-form">
            <div className="mb-1 h-px w-12 bg-[#C5A059]" />
            <h1 className="font-display text-4xl font-light tracking-tight text-[#0A1128]">Jemanden empfehlen</h1>
            <p className="text-base text-[#718096]">Wen könntest du dir als Berater:in vorstellen? Wir melden uns diskret.</p>
            <div className="space-y-4 pt-2">
              <Field label="Name" testid="input-karriere_name" value={karriereForm.name} onChange={(v) => setKarriereForm({ ...karriereForm, name: v })} placeholder="Vor- und Nachname" />
              <Field label="Kontakt (E-Mail oder Telefon)" testid="input-karriere_kontakt" value={karriereForm.kontakt} onChange={(v) => setKarriereForm({ ...karriereForm, kontakt: v })} placeholder="name@beispiel.de / +49 …" />
              <Field label="Notiz (optional)" testid="input-karriere_notiz" required={false} value={karriereForm.notiz} onChange={(v) => setKarriereForm({ ...karriereForm, notiz: v })} placeholder="Warum passt die Person?" />
            </div>
            <label className="flex cursor-pointer items-start gap-3 rounded-xl bg-white p-4 ring-1 ring-[#E2E8F0]">
              <Checkbox data-testid="karriere-zustimmung-checkbox" checked={zustimmung} onCheckedChange={setZustimmung} className="mt-0.5 h-5 w-5 border-[#C5A059] data-[state=checked]:border-[#C5A059] data-[state=checked]:bg-[#C5A059]" />
              <span className="text-sm leading-relaxed text-[#4A5568]">Ich habe die Zustimmung der Person eingeholt bzw. informiere sie, dass ich sie empfehle. Sie bestätigt die Weitergabe zusätzlich per E-Mail (DSGVO-konform).</span>
            </label>
            <button data-testid="submit-karriere-btn" type="submit" disabled={loading} className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[#0A1128] text-base font-semibold text-white transition-all duration-200 active:scale-[0.98] disabled:opacity-60">
              <UsersThree size={20} /> {loading ? "…" : isLoggedKunde ? "Empfehlung senden" : "Weiter"}
            </button>
          </motion.form>
        )}

        {step === "email" && (
          <motion.form key="email" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} onSubmit={requestCode} className="space-y-5" data-testid="email-step">
            <div className="mb-1 h-px w-12 bg-[#C5A059]" />
            <h1 className="font-display text-4xl font-light tracking-tight text-[#0A1128]">{flow === "kunde" && advisorPraemieAktiv !== false ? "Prämie sichern" : "Fast geschafft"}</h1>
            <p className="text-base text-[#718096]">{flow === "kunde" ? (advisorPraemieAktiv === false ? "Gib deine E-Mail an, damit du den Status deiner Empfehlung jederzeit verfolgen kannst." : "Gib deine E-Mail an, damit wir dir deine Prämie zuordnen und du den Fortschritt verfolgen kannst.") : "Gib deine E-Mail an, damit dein Berater sich bei dir melden kann."}</p>
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.1em] text-[#C5A059]">Dein Name</label>
              <div className="relative">
                <UserPlus size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A0AEC0]" />
                <Input data-testid="werber-name-input" type="text" required value={werberName} onChange={(e) => setWerberName(e.target.value)} placeholder="Vor- und Nachname" className="h-14 rounded-xl border-[#E2E8F0] bg-white pl-12 text-base focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]" />
              </div>
            </div>
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.1em] text-[#C5A059]">Deine E-Mail</label>
              <div className="relative">
                <EnvelopeSimple size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A0AEC0]" />
                <Input data-testid="email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@beispiel.de" className="h-14 rounded-xl border-[#E2E8F0] bg-white pl-12 text-base focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]" />
              </div>
            </div>
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.1em] text-[#C5A059]">Deine Telefonnummer (optional)</label>
              <div className="relative">
                <Phone size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A0AEC0]" />
                <Input data-testid="werber-tel-input" type="tel" value={werberTel} onChange={(e) => setWerberTel(e.target.value)} placeholder="+49 …" className="h-14 rounded-xl border-[#E2E8F0] bg-white pl-12 text-base focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]" />
              </div>
              <p className="mt-1.5 text-[11px] text-[#A0AEC0]">Damit dein Berater bei Rückfragen auch anrufen kann.</p>
            </div>
            <button data-testid="request-code-btn" type="submit" disabled={loading} className="h-14 w-full rounded-full bg-[#0A1128] text-base font-semibold text-white transition-all duration-200 active:scale-[0.98] disabled:opacity-60">
              {loading ? "Bitte warten…" : "Code anfordern"}
            </button>
          </motion.form>
        )}

        {step === "code" && (
          <motion.form key="code" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} onSubmit={verifyAndSubmit} className="space-y-5" data-testid="code-step">
            <div className="mb-1 h-px w-12 bg-[#C5A059]" />
            <h1 className="font-display text-4xl font-light tracking-tight text-[#0A1128]">Code eingeben</h1>
            <p className="text-base text-[#718096]">Gesendet an <span className="font-semibold text-[#0A1128]">{email}</span>.</p>
            <div className="rounded-xl border border-[#C5A059]/40 bg-[#FBF8F1] p-4" data-testid="code-sent-box">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#C5A059]">Code gesendet</p>
              <p className="mt-1 text-sm text-[#718096]">Wir haben dir einen 6-stelligen Code per E-Mail geschickt. Bitte prüfe dein Postfach (ggf. auch Spam).</p>
            </div>
            <div className="relative">
              <LockKey size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A0AEC0]" />
              <Input data-testid="code-input" type="text" inputMode="numeric" autoComplete="one-time-code" autoCorrect="off" autoCapitalize="off" spellCheck={false} name="one-time-code" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="6-stelliger Code" className="h-14 rounded-xl border-[#E2E8F0] bg-white pl-12 text-lg tracking-[0.3em] focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]" />
            </div>
            <button data-testid="verify-code-btn" type="submit" disabled={loading || code.length < 6} className="h-14 w-full rounded-full bg-[#C5A059] text-base font-semibold text-[#0A1128] shadow-[0_8px_30px_rgba(197,160,89,0.3)] transition-all duration-200 active:scale-[0.98] disabled:opacity-60">
              {loading ? "Wird gesendet…" : "Abschicken"}
            </button>
            <button type="button" data-testid="paste-code-btn" onClick={pasteCode} className="flex h-11 w-full items-center justify-center gap-2 rounded-full border border-[#0A1128]/15 bg-white text-sm font-semibold text-[#0A1128] transition-colors hover:bg-black/5 active:scale-[0.99]">
              <Clipboard size={17} /> Aus Zwischenablage einfügen
            </button>
            <button type="button" data-testid="resend-code-btn" onClick={resendCode} disabled={cooldown > 0 || loading} className="w-full text-center text-sm text-[#718096] transition-colors hover:text-[#0A1128] disabled:opacity-50">
              {cooldown > 0 ? `Code erneut senden (${cooldown}s)` : "Code erneut senden"}
            </button>
          </motion.form>
        )}

        {step === "done" && (
          <motion.div key="done" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="pt-8 text-center" data-testid="referral-success">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#276749]/10">
              <CheckCircle size={36} weight="fill" color="#276749" />
            </div>
            {flow === "karriere_interesse" ? (
              <>
                <h1 className="mt-6 font-display text-4xl font-light tracking-tight text-[#0A1128]">Danke für dein Interesse!</h1>
                <p className="mt-3 text-base text-[#718096]">Dein Berater meldet sich in Kürze bei dir und beantwortet alle Fragen rund um den Beraterberuf – ganz unverbindlich.</p>
              </>
            ) : flow === "karriere_empfehlung" ? (
              <>
                <h1 className="mt-6 font-display text-4xl font-light tracking-tight text-[#0A1128]">Empfehlung gesendet!</h1>
                <p className="mt-3 text-base text-[#718096]">Danke! Wir kümmern uns diskret um <span className="font-semibold text-[#0A1128]">{karriereForm.name}</span> und melden uns.</p>
                {karriereForm.kontakt.includes("@") ? (
                  <div className="mt-6 rounded-xl bg-[#FBF8F1] p-4 text-left text-sm text-[#4A5568] ring-1 ring-[#C5A059]/25" data-testid="dsgvo-hinweis-karriere">
                    <p className="flex items-start gap-2"><ShieldCheck size={18} color="#C5A059" className="mt-0.5 shrink-0" /> <span><strong className="text-[#0A1128]">Bitte kurz Bescheid geben:</strong> {karriereForm.name || "Die Person"} bekommt gerade eine E-Mail von uns, mit der sie noch nicht rechnet. Sag ihr am besten kurz, dass sie darin die DSGVO-Freigabe bestätigen muss – erst danach dürfen wir Kontakt aufnehmen.</span></p>
                  </div>
                ) : (
                  <div className="mt-6 rounded-xl bg-[#FBF8F1] p-4 text-left text-sm text-[#4A5568] ring-1 ring-[#C5A059]/25" data-testid="dsgvo-hinweis-karriere-tel">
                    <p className="flex items-start gap-2"><ShieldCheck size={18} color="#C5A059" className="mt-0.5 shrink-0" /> <span><strong className="text-[#0A1128]">Hinweis:</strong> Da nur eine Telefonnummer vorliegt, holt dein Berater die Zustimmung telefonisch ein. Sag {karriereForm.name || "der Person"} gern vorab kurz Bescheid, dass ein Anruf kommt.</span></p>
                  </div>
                )}
              </>
            ) : (
              <>
                <h1 className="mt-6 font-display text-4xl font-light tracking-tight text-[#0A1128]">Empfehlung gesendet!</h1>
                <p className="mt-3 text-base text-[#718096]">Wir haben <span className="font-semibold text-[#0A1128]">{form.kontakt_name}</span> eine kurze Bestätigungs-E-Mail geschickt. {advisorPraemieAktiv === false ? "Du kannst den Status jederzeit in deinem Bereich verfolgen." : "Sobald daraus ein Kunde wird, ist deine Prämie freigeschaltet."}</p>
                <div className="mt-6 rounded-xl bg-[#FBF8F1] p-4 text-left text-sm text-[#4A5568] ring-1 ring-[#C5A059]/25" data-testid="dsgvo-hinweis">
                  <p className="flex items-start gap-2"><ShieldCheck size={18} color="#C5A059" className="mt-0.5 shrink-0" /> <span><strong className="text-[#0A1128]">Bitte kurz Bescheid geben:</strong> {form.kontakt_name || "Die Person"} bekommt gerade eine E-Mail von uns, mit der sie noch nicht rechnet. Sag ihr am besten kurz, dass sie darin die DSGVO-Freigabe bestätigen muss – erst danach werden die Kontaktdaten weitergegeben.</span></p>
                </div>
                <p className="mt-4 text-sm font-medium italic text-[#8a6d2f]" data-testid="unternehmerlohn-quote">„Eine Empfehlung ist das höchste Gut – der wahre Unternehmerlohn."</p>
              </>
            )}
            <button onClick={() => navigate("/app")} className="mt-8 h-14 w-full rounded-full bg-[#0A1128] text-base font-semibold text-white transition-all active:scale-[0.98]">
              Zu meinem Bereich
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}

function ChoiceTile({ testid, icon, title, text, onClick }) {
  return (
    <button data-testid={testid} onClick={onClick} className="group flex w-full items-center gap-4 rounded-2xl border border-[#E2E8F0] bg-white p-5 text-left transition-all duration-200 hover:border-[#C5A059]/60 hover:shadow-[0_8px_30px_rgba(197,160,89,0.12)] active:scale-[0.99]">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#FBF8F1] ring-1 ring-[#C5A059]/20">{icon}</div>
      <div className="min-w-0 flex-1">
        <p className="text-base font-bold text-[#0A1128]">{title}</p>
        <p className="mt-0.5 text-sm leading-snug text-[#718096]">{text}</p>
      </div>
      <ArrowRight size={20} className="shrink-0 text-[#C5A059] transition-transform group-hover:translate-x-1" />
    </button>
  );
}

function Field({ label, testid, value, onChange, type = "text", placeholder, required = true }) {
  return (
    <div>
      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.1em] text-[#C5A059]">{label}</label>
      <Input data-testid={testid} type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-12 rounded-xl border-[#E2E8F0] bg-white text-base focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]" />
    </div>
  );
}
